import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import keycloak from "../../keycloak";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance, {
  updateToken,
  isTokenExpired,
  parseJwt,
} from "../../utils/axio-instance";
import { useDispatch } from "react-redux";
import { fetchProfile } from "../../actions/user.actions";
import {
  createResponseSession,
  saveSearchQuery,
} from "../../actions/search.actions";
import { storeChannels, getChannels, getDMChannelPosts, getChannelPosts, addPostToStore, updateTypingUsr, logoutFromMattermost, tokenExchange } from "../../actions/chat.actions";
import { computeGroups } from "../../utils/groups";
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const token = localStorage.getItem("token");
      return !!token && !isTokenExpired(token);
    } catch {
      return false;
    }
  });
  const [initialized, setInitialized] = useState(false);
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("groups") || "[]"); } catch { return []; }
  });
  const [globalMsg, setGlobalMsg] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ── WebSocket lifecycle ────────────────────────────────────────────────────
  // Held in refs, not state: these are imperative handles, and changing them
  // must not trigger a re-render.
  //
  // The ping interval MUST be tracked. It used to be created inside onopen and
  // never cleared, so every reconnect leaked another timer that went on calling
  // socket.send() against a dead socket for the life of the page -- the source
  // of "WebSocket is already in CLOSING or CLOSED state." Chat still worked,
  // because the newest socket was healthy; the noise came from the orphans.
  const socketRef = useRef(null);
  const reconnectRef = useRef(null);
  const closedByUsRef = useRef(false);

  // Tear down the socket for good -- used on logout and unmount, where we do
  // NOT want the onclose handler to reconnect.
  const closeSocket = () => {
    closedByUsRef.current = true;
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
    const sock = socketRef.current;
    socketRef.current = null;
    if (sock && (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING)) {
      sock.close(1000, "client closing");
    }
  };

  useEffect(() => {
    const session = new URLSearchParams(window.location.search).get("session");
    const mm_token = localStorage.getItem("mm_token");
    const ktdToken = localStorage.getItem("token");

    // Every signed-in user connects to messaging, because group messaging is
    // available to every role. This used to be gated on
    // `!roles.includes('staff')`, a substring test against the comma-joined roles
    // string -- so it also excluded `staff_member` and, unintentionally,
    // `builderstaff`. If the token cannot be obtained the app degrades: no
    // websocket, and the messages screen says it is not connected.
    if (session && mm_token == null && ktdToken) {
      axiosInstance.get(`/oauth/getToken/${session}`).then((res) => {
        const userToken = res.data.token; // ← Mattermost user token
        localStorage.setItem("mm_token", userToken);
        startWebSocket(userToken);
      }).catch((err) => {
        console.log("could not get messaging token", err?.message);
      });
    } else if (mm_token) {
      startWebSocket(mm_token);
    }

    return () => closeSocket();
  }, []);

  const getMMtoken = async () => {
    let mm_token = localStorage.getItem("mm_token");
    if(!mm_token){
        mm_token = await tokenExchange();
        if(mm_token != null && mm_token != 'null'){
          localStorage.setItem("mm_token", mm_token)
        }else{
          mm_token = null;
        }
    }

    if(mm_token){
      startWebSocket(mm_token);
      return;
    }
   
    //console.log("calling mattermost api to get token");
    //window.location.href = `${process.env.REACT_APP_DYNAMO_API_URL}/oauth/getmmtoken?user_id=${localStorage.getItem("userid")}`;
  };

  useEffect(() => {
    if (!initialized && location.pathname === "/login") {
      setInitialized(true);
      
      // Add timeout for Keycloak initialization
      const timeoutId = setTimeout(() => {
        console.error("Keycloak initialization timeout");
        setInitialized(false);
        // Fallback to manual redirect
        window.location.href = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${process.env.REACT_APP_REALM}/protocol/openid-connect/auth?client_id=${process.env.REACT_APP_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin)}&response_type=code&scope=openid`;
      }, 10000); // 10 second timeout
      
      keycloak
        .init({
          onLoad: "login-required",
          prompt: 'login',
          checkLoginIframe: true
        })
        .then(async (authenticated) => {
          clearTimeout(timeoutId); // Clear timeout on success
          console.log("authenticated", authenticated);
          setIsAuthenticated(authenticated);
          if (authenticated) {
            setupTokenRefresh();
            localStorage.setItem("token", keycloak.token);
            localStorage.setItem("refreshToken", keycloak.refreshToken);
            localStorage.setItem("idToken", keycloak.idToken);
            localStorage.removeItem("usrgroups");
            let parsedToken = parseJwt(keycloak.token);
            let federatedIsNew = false;
            // Social-login (federated) users are created by the Keycloak broker
            // flow and never pass through signup, so their first token has no
            // `user_id` claim and no app-side records. Provision them (people
            // index + KC `userId` attribute) then force a token refresh so the
            // refreshed token carries `user_id`. Password logins already have it
            // and skip this entirely.
            if (!parsedToken.user_id) {
              try {
                updateToken(keycloak.token); // authorize axios with the current KC token
                const provResp = await axiosInstance.post("/users/provision-federated");
                console.log("provision-federated response:", provResp?.data);
                federatedIsNew = provResp?.data?.isNew === true; // first-time → show onboarding
                await keycloak.updateToken(-1); // force refresh → new token has user_id
                localStorage.setItem("token", keycloak.token);
                localStorage.setItem("refreshToken", keycloak.refreshToken);
                parsedToken = parseJwt(keycloak.token);
                console.log("user_id after provisioning+refresh:", parsedToken.user_id);
              } catch (e) {
                console.error(
                  "federated user provisioning failed:",
                  e?.response?.status,
                  e?.response?.data || e?.message
                );
              }
            }
            localStorage.setItem("userid", parsedToken.user_id);
            localStorage.setItem("kc_uid", parsedToken.sub);
            const clientRoles =
              (parsedToken.resource_access || {})[process.env.REACT_APP_CLIENT_ID] ||
              {};
            const roleList = clientRoles?.roles || ["buyer"];
            localStorage.setItem("roles", roleList);
            // Authorize axios and kick off the profile load BEFORE any awaited
            // call — a failed enums request must not abort the login bootstrap.
            updateToken(keycloak.token);
            dispatch(fetchProfile(parsedToken.user_id));
            // Compute and store group membership (role→group map from enums API)
            let groups = [];
            try {
              groups = await computeGroups(Array.isArray(roleList) ? roleList : [roleList]);
            } catch (e) {
              console.error("computeGroups failed, continuing without groups:", e);
            }
            localStorage.setItem("groups", JSON.stringify(groups));
            setGroups(groups);
            // First-time social-login user → send them to the one-time role /
            // services onboarding screen (defaults to buyer) before the app.
            if (federatedIsNew) {
              navigate("/welcome");
              return;
            }
            const responseFromSession = localStorage.getItem("requestResponse");
            // Every role gets a Mattermost token -- group messaging is available
            // to all users, so admin and staff need one too. getMMtoken() leaves
            // mm_token unset if the exchange fails, which the messages screen
            // reports rather than breaking.
            getMMtoken();
            //console.log("saving response after login");
            if (localStorage.getItem("requestResponse")) {
              //console.log("saving response after login",  responseFromSession);
              const responseDetailsJosn = JSON.parse(responseFromSession);
              dispatch(createResponseSession(responseDetailsJosn));
              localStorage.removeItem("requestRole");
              navigate("/buy/start");
              return;
            }
            if (localStorage.hasOwnProperty("searchQuery")) {
              dispatch(
                saveSearchQuery(JSON.parse(localStorage.getItem("searchQuery")))
              );
              localStorage.removeItem("searchQuery");
            }
            try {
              const requestedPage = localStorage.getItem("requestedPage");
              if (requestedPage) {
                navigate(requestedPage);
                localStorage.removeItem("requestedPage");
                return;
              }
                //const curUserRole = clientRoles?.roles[0];
              
                navigate("/buy/start");
                return;
             
            } catch (e) {
              console.log(e);
              navigate("/buy/start");
            }
          } else {
            keycloak.login().catch((loginError) => {
              console.error("Keycloak login error:", loginError);
              // Reset initialized state to allow retry
              setInitialized(false);
              // Fallback to manual redirect if Keycloak login fails
              window.location.href = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${process.env.REACT_APP_REALM}/protocol/openid-connect/auth?client_id=${process.env.REACT_APP_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin)}&response_type=code&scope=openid`;
            });
          }
        })
        .catch((e) => {
          clearTimeout(timeoutId); // Clear timeout on error
          console.error("Keycloak initialization error:", e);
          // Reset initialized state to allow retry
          setInitialized(false);
          // Fallback to manual redirect if Keycloak fails
          if (location.pathname === "/login") {
            window.location.href = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${process.env.REACT_APP_REALM}/protocol/openid-connect/auth?client_id=${process.env.REACT_APP_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin)}&response_type=code&scope=openid`;
          }
        });
    } else if (location.pathname !== "/signup") {
      const storedUserInfo = localStorage.getItem("userid");
      if (storedUserInfo && localStorage.getItem("token")) {
        if (isTokenExpired(localStorage.getItem("token"))) {
          setIsAuthenticated(false);
          navigate("/login");
        } else if (storedUserInfo) {
          // Hydrate auth state from the valid stored token so route guards
          // (PrivateRoute/GroupRoute/RoleRoute) don't bounce a logged-in user
          // to /login after a full page reload.
          setIsAuthenticated(true);
          updateToken(localStorage.getItem("token"));
        }
      } else {
        // navigate('/login');
      }
    }
  }, [location.pathname]);

  // Handle browser visibility change (tab switching, idle periods)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page is now visible, check token validity
        const currentToken = localStorage.getItem("token");
        if (currentToken && isTokenExpired(currentToken)) {
          console.warn("Token expired during idle period, forcing logout");
          logout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Handle user interactions after idle periods
  useEffect(() => {
    const handleUserInteraction = () => {
      const currentToken = localStorage.getItem("token");
      if (currentToken && isTokenExpired(currentToken)) {
        console.warn("Token expired on user interaction, forcing logout");
        logout();
      }
    };

    // Add event listeners for user interactions
    const events = ['click', 'touchstart', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, []);

  const setupTokenRefresh = () => {
    setInterval(async () => {
      try {
        // Check if token is already expired before attempting refresh
        const currentToken = localStorage.getItem("token");
        if (currentToken && isTokenExpired(currentToken)) {
          console.warn("Token is expired, forcing logout");
          await logout();
          return;
        }
        
        const refreshed = await keycloak.updateToken(60); // Refresh if token expires in 60 seconds
        if (refreshed) {
          console.log("Token refreshed successfully");
          localStorage.setItem("token", keycloak.token);
          localStorage.setItem("refreshToken", keycloak.refreshToken);
          updateToken(keycloak.token);
        } else {
          console.info("Token not refreshed, still valid.");
        }
      } catch (error) {
        console.error("Failed to refresh token", error);
        // Force logout on refresh failure to prevent stuck state
        await logout();
      }
    }, 60000); // Check token refresh every 60 seconds
  };

  const getPostLogoutRedirectUri = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    return `${baseUrl.replace(/\/$/, "")}/buy/start`;
  };

  const buildKeycloakLogoutUrl = (idTokenHint) => {
    const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL?.replace(/\/$/, "");
    const realm = process.env.REACT_APP_REALM;

    if (!keycloakUrl || !realm) {
      return null;
    }

    const logoutUrl = new URL(
      `${keycloakUrl}/realms/${encodeURIComponent(realm)}/protocol/openid-connect/logout`
    );

    logoutUrl.searchParams.set(
      "post_logout_redirect_uri",
      getPostLogoutRedirectUri()
    );

    if (process.env.REACT_APP_CLIENT_ID) {
      logoutUrl.searchParams.set("client_id", process.env.REACT_APP_CLIENT_ID);
    }

    if (idTokenHint) {
      logoutUrl.searchParams.set("id_token_hint", idTokenHint);
    }

    return logoutUrl.toString();
  };

  const clearAuthState = () => {
    if (typeof keycloak.clearToken === "function") {
      keycloak.clearToken();
    }

    setIsAuthenticated(false);
    setUser(null);
    updateToken("");

    [
      "token",
      "refreshToken",
      "idToken",
      "userid",
      "kc_uid",
      "roles",
      "mm_token",
      "usrgroups",
    ].forEach((key) => localStorage.removeItem(key));
  };

  const logout = async () => {
    // Close first, so the socket does not reconnect against a revoked session.
    closeSocket();
    const idTokenHint = keycloak?.idToken || localStorage.getItem("idToken");
    const keycloakLogoutUrl = buildKeycloakLogoutUrl(idTokenHint);

    try {
      await logoutFromMattermost();
    } catch (e) {
      console.error("Mattermost logout error:", e);
    } finally {
      clearAuthState();
    }

    if (keycloakLogoutUrl) {
      window.location.assign(keycloakLogoutUrl);
      return;
    }

    window.location.assign(getPostLogoutRedirectUri());
  };

  const checkLogIn = () => {
    const loggedIn = !!localStorage.getItem("userid");
    if (loggedIn) {
      setUser({
        accessToken: localStorage.getItem("token"),
        refreshToken: localStorage.getItem("refreshToken"),
        userId: localStorage.getItem("userid"),
      });
    }
    return loggedIn;
  };

  async function startWebSocket(token) {
    // This function does two jobs: load channel/post data, and open the socket.
    // They must stay independent. Callers such as getMMtoken() rely on it to
    // refresh channels and prefetch posts even when a socket is already live --
    // guarding the whole function on "socket exists" stopped posts loading at
    // all. The single-socket guard therefore lives further down, immediately
    // before the socket is created.

    // getChannels rethrows when the user has no Mattermost team yet. Now that
    // every role connects, that is a normal case (a brand-new account, or one
    // never provisioned in Mattermost) -- bail out quietly instead of leaving an
    // unhandled rejection and a dead socket.
    let channels;
    try {
      channels = await getChannels(token);
    } catch (err) {
      console.log("messaging unavailable for this account:", err?.message);
      return;
    }
    if (!Array.isArray(channels)) return;

    dispatch(storeChannels(channels));
    // Private channels only -- group channels are created private, and this also
    // skips town-square/off-topic noise.
    const dmChannelIds = channels
      .filter((c) => c.type === "P")
      .map((c) => c.id);
      dmChannelIds.forEach(async (channelId) => {
        try {
          const msgs =  await getDMChannelPosts(token, channelId);
          dispatch(getChannelPosts(channelId, msgs));
        } catch (err) {
          console.log("could not prefetch posts for channel", channelId, err?.message);
        }
      });
    // console.log("mmtoken to connect", token);
    // ── Socket, from here down ───────────────────────────────────────────────
    // startWebSocket is called from the mount effect AND from getMMtoken(), so
    // without this guard a user ends up with several live sockets, each with its
    // own ping timer and reconnect chain. There is no await between this check
    // and the assignment below, so two concurrent callers cannot both pass it.
    const existing = socketRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
      return;
    }
    closedByUsRef.current = false;
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }

    const socket = new WebSocket(
      `wss://${process.env.REACT_APP_MM_URL}/api/v4/websocket?connection_id=&sequence_number=0`
    );
    socketRef.current = socket;

    // Owned by THIS socket, in a closure rather than a shared ref: if two
    // sockets ever overlap, each still clears its own timer instead of one
    // overwriting the other's handle and leaking it.
    let pingTimer = null;

    socket.onopen = () => {
      console.log("WS connected");

      socket.send(
        JSON.stringify({
          seq: 1,
          action: "authentication_challenge",
          data: { token },
        })
      );

      // Keepalive. Cleared in this socket's own onclose -- see the note where
      // the refs are declared. The readyState check is belt-and-braces: if this
      // timer ever outlives its socket again, it stops itself instead of
      // throwing on every tick.
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = setInterval(() => {
        if (socket.readyState !== WebSocket.OPEN) {
          clearInterval(pingTimer);
          pingTimer = null;
          return;
        }
        socket.send(JSON.stringify({
          seq: Date.now(),
          action: "ping",
          data: {}
        }));
      }, 30000);
    };

    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.event === 'hello') {
        console.log("WS hello, socket connected");
      } else if (msg.event === "posted") {
        const post = JSON.parse(msg.data.post);
        console.log("New message:", post.channel_id, post);
        dispatch(addPostToStore(post.channel_id, post));
      } else if (msg.event === "typing") {
        const { userId } = msg.data;
        console.log("typing...", userId);
        dispatch(updateTypingUsr(userId));
      }else{
        console.log("error in ws", msg);
      }
    };

    socket.onclose = (e) => {
      console.log("WS closed", e.code);

      // Always stop this socket's ping timer, whatever happens next.
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }

      // A socket that has already been replaced must not drive reconnects --
      // otherwise a late close from an old socket spawns a second chain.
      if (socketRef.current !== socket) return;
      socketRef.current = null;

      // Deliberate close (logout, unmount): stay closed.
      if (closedByUsRef.current) return;

      clearTimeout(reconnectRef.current);
      reconnectRef.current = setTimeout(() => startWebSocket(token), 2000);
    };
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        globalMsg,
        setGlobalMsg,
        user,
        setUser,
        groups,           // string[] — e.g. ['admin', 'staff']
        logout,
        checkLogIn,
        getMMtoken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
